/**
 * Edit mode renderer — displays vertices, edges, and faces as selectable
 * elements when the user is in edit mode on a scene object.
 *
 * Uses polygon topology for all user-facing display: edges follow polygon
 * boundaries (no triangle diagonals on quads), face selection targets
 * whole polygons, and edge selection finds polygon edges.
 *
 * With shared-vertex topology, vertices at the same position share a single
 * index, so display and selection operate on shared indices directly.
 */

import { type ThreeEvent, useFrame, useThree } from "@react-three/fiber";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { LineSegments2 } from "three/examples/jsm/lines/webgpu/LineSegments2.js";
import * as THREE from "three/webgpu";
import { acceleratedRaycast } from "three-mesh-bvh";
import {
  getCollectionForObject,
  getSceneObjectWorldTransform,
  worldTransformForSceneObject,
} from "../app/collectionGraph.ts";
import { resolveSceneObjectEditSession } from "../app/editSession.ts";
import { useStore } from "../app/store.ts";
import type {
  EditClickHit,
  EditClickModifiers,
} from "../app/storeEditOps/index.ts";
import { registerEditRaycast } from "../app/storeEditOps/index.ts";
import type { EdgeSlideRail } from "../app/storeTypes.ts";
import { useResolvedLiveEditGeometry } from "../app/useLiveEditPreview.ts";
import type { BlendFollowObjectModifier } from "../blend-reader/types/BlendModifier.ts";
import { getBone } from "../mannequin/boneRegistry.ts";
import { vertexCentroid } from "../modeling/editSelection.ts";
import { expandForRendering } from "../modeling/loopExpand.ts";
import { previewEdgeLoop } from "../modeling/meshOps.ts";
import {
  classifyPolySelection,
  closestEdgeNearFace,
  closestEdgeOnPolyFace,
  edgeVertices,
  findEdgeIndex,
  numEdgeKey,
  topologyFaceVerts,
} from "../modeling/topology.ts";
import type {
  PolygonTopology,
  RenderTriangleIndices,
} from "../modeling/types.ts";
import {
  asLoopNormals,
  EDGE_SEAM,
  EDGE_SHARP,
  EDGE_WIRE,
} from "../modeling/types.ts";
import { useAsyncBoundsTree } from "./asyncBoundsTree.ts";
import { addComponentUpdateRanges } from "./bufferUpdateRanges.ts";
import { CurveEditOverlay } from "./CurveEditOverlay.tsx";
import {
  _dummy,
  createDotGeometry,
  createOverlayFaceMaterial,
  createOverlayLineMaterial,
  dotBaseScale,
  dotMaterial,
  dotXray,
  setDotColor,
  setDotNormals,
  VERTEX_RADIUS,
  VERTEX_SCREEN_PX,
} from "./editDots.ts";
import {
  getCachedTriToPolyMap,
  getIdentityVertexIndices,
  getNearestVertexDistances,
} from "./editMeshCache.ts";
import {
  buildBudgetedFaceOverlay,
  buildBudgetedVertexOverlay,
  initialRefinementBudget,
  nextRefinementBudget,
  overlayBudgetForViewport,
} from "./editOverlayBudget.ts";
import {
  type EditSelectionCycleCandidate,
  noteNonCycleEditClick,
  resetEditSelectionCycle,
  resolveEditSelectionCycle,
} from "./editSelectionCycle.ts";
import { FaceGroupColorOverlay } from "./FaceGroupColorOverlay.tsx";
import { useDispose, useDisposeMany } from "./hooks/useDispose.ts";
import {
  HOLD_TO_ADD_MS,
  TAP_DRAG_THRESHOLD_SQ,
} from "./interactionConstants.ts";
import { MaterialSlotColorOverlay } from "./MaterialSlotColorOverlay.tsx";
import { mergeModifierStack } from "./modifiers/evaluation.ts";
import {
  buildVertexSegmentIndex,
  resolveFatLineUpdateRanges,
  setFatLinePositions,
  writeFatLineSegmentPositions,
} from "./objectHelperLines.ts";
import { makeScratch } from "./scratch.ts";
import { screenToWorldRadius } from "./screenRadius.ts";
import { VertexGroupColorOverlay } from "./VertexGroupColorOverlay.tsx";
import { WireEdgeHitMesh } from "./WireEdgeHitMesh.tsx";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Follow Object constraint — module-level temp objects (never reallocated)
// ---------------------------------------------------------------------------

const follow = makeScratch({
  pos: "vec3",
  quat: "quat",
  quat2: "quat",
  euler: "euler",
  scale: "vec3",
});

// ---------------------------------------------------------------------------
// Occlusion mesh — allows vertex selection to test depth against the surface
// ---------------------------------------------------------------------------

let _occlusionMesh: THREE.Mesh | null = null;
let _occlusionHiddenFaces: Set<number> = new Set();
let _occlusionTriToPolyMap: Uint32Array | null = null;
function registerOcclusionMesh(mesh: THREE.Mesh | null) {
  _occlusionMesh = mesh;
}
function registerOcclusionHidden(
  hidden: Set<number>,
  triMap: Uint32Array | null,
) {
  _occlusionHiddenFaces = hidden;
  _occlusionTriToPolyMap = triMap;
}

// Scratch ray reused by isWorldPointOccluded. Box-select calls it many times
// per frame — allocating a new Ray + Vector3 per vertex churned the GC.
// Also: scratch vectors for per-frame dot-scaling useFrame callbacks, reused
// to avoid allocating fresh Vector3s every frame (60 Hz × 2 dot meshes).
const occ = makeScratch({
  raycaster: "raycaster",
  scratchRay: "ray",
  worldPos: "vec3",
});

/**
 * Test whether a world-space point is occluded by the edit mesh surface,
 * as seen from a camera origin. Exported for use by BoxSelectHandler.
 */
export function isWorldPointOccluded(
  worldPos: THREE.Vector3,
  cameraOrigin: THREE.Vector3,
  adjacentVertices?: ReadonlyArray<number>,
): boolean {
  const ray = occ.scratchRay;
  ray.origin.copy(cameraOrigin);
  ray.direction.copy(worldPos).sub(cameraOrigin).normalize();
  const dist = cameraOrigin.distanceTo(worldPos);
  return isOccludedByMesh(ray, dist, adjacentVertices);
}

/**
 * Check whether a world-space point is occluded by the edit mesh surface.
 * Returns true if there is any triangle intersection (front or back face)
 * closer to the camera origin than the candidate point.
 *
 * @param adjacentVertices — optional set of vertex indices that belong to the
 *   candidate element. Triangles containing any of these vertices are skipped,
 *   preventing faces at a reflex (concave) angle from falsely occluding a
 *   vertex at the crease.
 */
function isOccludedByMesh(
  ray: THREE.Ray,
  candidateDistance: number,
  adjacentVertices?: ReadonlyArray<number>,
): boolean {
  const mesh = _occlusionMesh;
  if (!mesh) return false;
  const mat = mesh.material as THREE.Material;
  const wasVisible = mat.visible;
  mat.visible = true;
  occ.raycaster.ray.copy(ray);
  occ.raycaster.near = 0;
  occ.raycaster.far = Infinity;
  const hits = occ.raycaster.intersectObject(mesh);
  mat.visible = wasVisible;

  const index = mesh.geometry.index;
  const indexArr = index?.array;

  // Tolerance scales with the dot's world-space size so that vertices at
  // convex edges/corners — where the mesh surface intersection is nearly
  // coincident with the vertex — aren't falsely reported as occluded.
  const tolerance = Math.max(1e-4, dotBaseScale.value * 2);
  for (const hit of hits) {
    if (!hit.face) continue;
    // Any surface hit (front or back face) closer than the candidate → occluded.
    // Both sides occlude because the mesh renders DoubleSide.
    if (hit.distance < candidateDistance - tolerance) {
      // Skip triangles belonging to hidden faces — they don't occlude anything.
      if (
        _occlusionHiddenFaces.size > 0 &&
        _occlusionTriToPolyMap &&
        hit.faceIndex != null
      ) {
        if (_occlusionHiddenFaces.has(_occlusionTriToPolyMap[hit.faceIndex]))
          continue;
      }
      // Skip triangles adjacent to the candidate vertex — at a reflex angle
      // the adjacent face is closer but does not actually occlude the vertex.
      if (adjacentVertices && indexArr && hit.faceIndex != null) {
        const base = hit.faceIndex * 3;
        const a = indexArr[base],
          b = indexArr[base + 1],
          c = indexArr[base + 2];
        if (
          adjacentVertices.includes(a) ||
          adjacentVertices.includes(b) ||
          adjacentVertices.includes(c)
        ) {
          continue;
        }
      }
      return true;
    }
  }
  return false;
}

const COLOR_DEFAULT = "#444444";
const COLOR_SELECTED = "#ff9922";
const COLOR_EDGE_HIGHLIGHT = "#ff9922";
const COLOR_FACE_HIGHLIGHT = "#ff9922";
const EDGE_COLOR = "#555555";
const EDGE_COLOR_PROMINENT = "#888888";
// Blender-parity flagged-edge tints (seam red-orange, sharp cyan). The seam
// hue is deliberately redder than COLOR_EDGE_HIGHLIGHT so marks stay legible
// on top of a selection.
const COLOR_EDGE_SEAM = "#e8551a";
const COLOR_EDGE_SHARP = "#39c8d8";
const COLOR_RAIL = "#5aa0e8";
const COLOR_GRAB_DOT = "#5aa0e8";

export function updateSharedVertexPositions(
  attribute: THREE.BufferAttribute,
  vertices: Float32Array,
  dirtyVertices?: Uint32Array | null,
  dirtyVertexPositions?: Float32Array | null,
): void {
  const posArr = attribute.array as Float32Array;
  if (dirtyVertices && dirtyVertices.length > 0) {
    let min = vertices.length / 3;
    let max = -1;
    for (let i = 0; i < dirtyVertices.length; i++) {
      const vi = dirtyVertices[i]!;
      const src = dirtyVertexPositions ? i * 3 : vi * 3;
      const base = vi * 3;
      posArr[base] = dirtyVertexPositions
        ? dirtyVertexPositions[src]!
        : vertices[base]!;
      posArr[base + 1] = dirtyVertexPositions
        ? dirtyVertexPositions[src + 1]!
        : vertices[base + 1]!;
      posArr[base + 2] = dirtyVertexPositions
        ? dirtyVertexPositions[src + 2]!
        : vertices[base + 2]!;
      if (vi < min) min = vi;
      if (vi > max) max = vi;
    }
    if (min <= max) addComponentUpdateRanges(attribute, dirtyVertices, 3);
  } else {
    posArr.set(vertices);
    attribute.updateRanges.length = 0;
  }
  attribute.needsUpdate = true;
}

function buildDirtyVertexPositionMap(
  dirtyVertices: Uint32Array | null | undefined,
  dirtyVertexPositions: Float32Array | null | undefined,
): Map<number, number> | null {
  if (!dirtyVertices || !dirtyVertexPositions) return null;
  return new Map<number, number>(
    Array.from(dirtyVertices, (vertexIndex, i) => [vertexIndex, i * 3]),
  );
}

function readVertexPosition(
  vertices: Float32Array,
  vertexIndex: number,
  dirtyVertexOffsets: Map<number, number> | null,
  dirtyVertexPositions: Float32Array | null | undefined,
): [number, number, number] {
  const dirtyOffset = dirtyVertexOffsets?.get(vertexIndex);
  if (dirtyOffset != null && dirtyVertexPositions) {
    return [
      dirtyVertexPositions[dirtyOffset]!,
      dirtyVertexPositions[dirtyOffset + 1]!,
      dirtyVertexPositions[dirtyOffset + 2]!,
    ];
  }
  const base = vertexIndex * 3;
  return [vertices[base]!, vertices[base + 1]!, vertices[base + 2]!];
}

function useRefinedOverlayBudget(
  targetBudget: number,
  depsKey: string,
): number {
  const [budget, setBudget] = useState(() =>
    initialRefinementBudget(targetBudget),
  );
  useEffect(() => {
    // The key intentionally resets progressive refinement when the viewed
    // topology/selection/visibility set changes, even if the numeric budget
    // stays constant.
    void depsKey;
    let current = initialRefinementBudget(targetBudget);
    setBudget(current);
    if (current >= targetBudget) return;
    let raf = 0;
    const step = () => {
      current = nextRefinementBudget(current, targetBudget);
      setBudget(current);
      if (current < targetBudget) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [targetBudget, depsKey]);
  return budget;
}

function markInstanceMatrixUpdateRange(
  mesh: THREE.InstancedMesh,
  minIndex: number,
  maxIndex: number,
): void {
  if (minIndex > maxIndex) return;
  const attribute = mesh.instanceMatrix;
  attribute.updateRanges.length = 0;
  attribute.addUpdateRange(minIndex * 16, (maxIndex - minIndex + 1) * 16);
  attribute.needsUpdate = true;
}

function markInstanceMatrixUpdateRanges(
  mesh: THREE.InstancedMesh,
  indices: ArrayLike<number>,
): void {
  if (indices.length === 0) return;
  const attribute = mesh.instanceMatrix;
  addComponentUpdateRanges(attribute, indices, 16);
  attribute.needsUpdate = true;
}

interface VertexFaceInstanceIndex {
  offsets: Uint32Array;
  instanceIndices: Uint32Array;
}

function buildVertexFaceInstanceIndex(
  topology: PolygonTopology,
  visibleFaces: Uint32Array,
  vertexCount: number,
): VertexFaceInstanceIndex {
  const counts = new Uint32Array(vertexCount);
  const { offsets, corners } = topology;
  for (
    let instanceIndex = 0;
    instanceIndex < visibleFaces.length;
    instanceIndex++
  ) {
    const faceIndex = visibleFaces[instanceIndex]!;
    for (
      let cornerIndex = offsets[faceIndex];
      cornerIndex < offsets[faceIndex + 1];
      cornerIndex++
    ) {
      counts[corners[cornerIndex]]++;
    }
  }
  const vertexOffsets = new Uint32Array(vertexCount + 1);
  for (let vertexIndex = 0; vertexIndex < vertexCount; vertexIndex++) {
    vertexOffsets[vertexIndex + 1] =
      vertexOffsets[vertexIndex] + counts[vertexIndex];
  }
  const instanceIndices = new Uint32Array(vertexOffsets[vertexCount]);
  counts.fill(0);
  for (
    let instanceIndex = 0;
    instanceIndex < visibleFaces.length;
    instanceIndex++
  ) {
    const faceIndex = visibleFaces[instanceIndex]!;
    for (
      let cornerIndex = offsets[faceIndex];
      cornerIndex < offsets[faceIndex + 1];
      cornerIndex++
    ) {
      const vertexIndex = corners[cornerIndex]!;
      instanceIndices[vertexOffsets[vertexIndex] + counts[vertexIndex]++] =
        instanceIndex;
    }
  }
  return { offsets: vertexOffsets, instanceIndices };
}

function writeFaceDotMatrix(
  mesh: THREE.InstancedMesh,
  instanceIndex: number,
  faceIndex: number,
  vertices: Float32Array,
  topology: PolygonTopology,
  dirtyVertexOffsets: Map<number, number> | null = null,
  dirtyVertexPositions: Float32Array | null = null,
): void {
  const { offsets, corners } = topology;
  const start = offsets[faceIndex]!;
  const end = offsets[faceIndex + 1]!;
  if (end <= start) return;
  let x = 0;
  let y = 0;
  let z = 0;
  for (let cornerIndex = start; cornerIndex < end; cornerIndex++) {
    const [vx, vy, vz] = readVertexPosition(
      vertices,
      corners[cornerIndex]!,
      dirtyVertexOffsets,
      dirtyVertexPositions,
    );
    x += vx;
    y += vy;
    z += vz;
  }
  const scale = 1 / (end - start);
  _faceDummy.position.set(x * scale, y * scale, z * scale);
  _faceDummy.scale.setScalar(1);
  _faceDummy.updateMatrix();
  mesh.setMatrixAt(instanceIndex, _faceDummy.matrix);
}

// ---------------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------------

export function EditModeRenderer() {
  const editMode = useStore((s) => s.editMode);
  const baseObj = useStore((s) =>
    editMode ? s.sceneObjects.get(editMode.objectId) : undefined,
  );
  const storeEditSession = useStore((s) =>
    editMode?.objectId && s.activeEditSession?.objectId === editMode.objectId
      ? s.activeEditSession
      : null,
  );
  const activeEditSession = storeEditSession;
  const dirtyVertices = activeEditSession?.dirtyVertices ?? null;
  // Collections map subscription — ensures the overlay re-renders when the
  // parent collection's transform changes (e.g. user drags the owning group
  // outside an active drag). The per-frame useFrame below still reads live
  // state, but the initial render props need a reactive source too.
  const collections = useStore((s) => s.collections);

  // X-ray state — used by child components for depth testing and culling.
  // When off: dots are GPU-culled via per-instance normals (back-face test
  // in the vertex shader), face highlights use FrontSide, and edges/dots
  // use depthTest:true. When on: culling is disabled so all elements show.
  const xray = editMode?.xray ?? false;

  // Sync the shared dot xray uniform so the vertex shader can skip
  // back-face culling when x-ray is active.
  useEffect(() => {
    dotXray.value = xray ? 1 : 0;
  }, [xray]);

  // Follow Object constraint — keep the edit overlay co-located with
  // PrimitiveRenderer's visually-constrained position so vertex dots and
  // the transparent surface mesh are always in the same world-space position.
  const editGroupRef = useRef<THREE.Group>(null);
  const editObjId = editMode?.objectId;
  const objModifierOverrides = useStore((s) =>
    editObjId ? s.modifierOverrides.get(editObjId) : undefined,
  );
  const followConstraints = useMemo(() => {
    if (!objModifierOverrides) return [] as BlendFollowObjectModifier[];
    return mergeModifierStack([], objModifierOverrides).filter(
      (m): m is BlendFollowObjectModifier =>
        m.kind === "followObject" && m.enabled && !!m.targetObjectName,
    );
  }, [objModifierOverrides]);
  const followConstraintsRef = useRef(followConstraints);
  followConstraintsRef.current = followConstraints;

  useFrame(() => {
    const g = editGroupRef.current;
    if (!g || !editObjId) return;
    const st = useStore.getState();
    // World transform composes collection.transform ∘ obj local so the edit
    // overlay tracks the rendered mesh when the object is a collection member
    // (e.g. a child of a baked-then-applied group generator). Reads fresh
    // each frame so collection drags stay in sync with the drag being
    // previewed by the primitive renderer.
    const wt = getSceneObjectWorldTransform(
      st.sceneObjects,
      st.collections,
      editObjId,
    );
    if (!wt) return;
    let [px, py, pz] = wt.position;
    let [rx, ry, rz] = wt.rotation;
    let [sx, sy, sz] = wt.scale;

    const constraints = followConstraintsRef.current;
    if (constraints.length > 0) {
      const allObjs = useStore.getState().sceneObjects;
      for (const mod of constraints) {
        let target: ReturnType<typeof allObjs.get> | undefined;
        for (const o of allObjs.values()) {
          if (o.name === mod.targetObjectName) {
            target = o;
            break;
          }
        }
        if (!target) continue;

        if (mod.targetBoneName) {
          const bone = getBone(target.id, mod.targetBoneName);
          if (!bone) continue;
          if (mod.followPosition) {
            bone.getWorldPosition(follow.pos);
            px += follow.pos.x;
            py += follow.pos.y;
            pz += follow.pos.z;
          }
          if (mod.followRotation) {
            bone.getWorldQuaternion(follow.quat);
            follow.euler.set(rx, ry, rz);
            follow.quat2.setFromEuler(follow.euler);
            follow.quat.multiply(follow.quat2);
            follow.euler.setFromQuaternion(follow.quat);
            rx = follow.euler.x;
            ry = follow.euler.y;
            rz = follow.euler.z;
          }
          if (mod.followScale) {
            bone.getWorldScale(follow.scale);
            sx *= follow.scale.x;
            sy *= follow.scale.y;
            sz *= follow.scale.z;
          }
        } else {
          if (mod.followPosition) {
            px += target.position[0];
            py += target.position[1];
            pz += target.position[2];
          }
          if (mod.followRotation) {
            follow.euler.set(
              target.rotation[0],
              target.rotation[1],
              target.rotation[2],
            );
            follow.quat.setFromEuler(follow.euler);
            follow.euler.set(rx, ry, rz);
            follow.quat2.setFromEuler(follow.euler);
            follow.quat.multiply(follow.quat2);
            follow.euler.setFromQuaternion(follow.quat);
            rx = follow.euler.x;
            ry = follow.euler.y;
            rz = follow.euler.z;
          }
          if (mod.followScale) {
            sx *= target.scale[0];
            sy *= target.scale[1];
            sz *= target.scale[2];
          }
        }
      }
    }

    g.position.set(px, py, pz);
    g.rotation.set(rx, ry, rz);
    g.scale.set(sx, sy, sz);
  });

  if (!editMode) return null;
  if (!baseObj) return null;
  const obj = resolveSceneObjectEditSession(baseObj, activeEditSession);

  // World transform (collection.transform ∘ obj local) — the frame that the
  // primitive renderer draws in. Edit overlays render in this same frame so
  // vertex dots, edges, and faces sit on top of the rendered mesh when the
  // object is a collection member. `obj` is already reactive via useStore, so
  // composing from it + the collection avoids a redundant map lookup.
  const worldTransform = worldTransformForSceneObject(
    obj,
    getCollectionForObject(collections, editMode.objectId),
  );

  // Curve objects get their own edit overlay
  if (editMode.isCurve && obj.curveData && editMode.curveEditVertices) {
    return (
      <CurveEditOverlay
        curveData={obj.curveData}
        editVertices={editMode.curveEditVertices}
        selectedVertices={editMode.selectedVertices}
        objectPosition={worldTransform.position}
        objectRotation={worldTransform.rotation}
        objectScale={worldTransform.scale}
      />
    );
  }

  const granularity = editMode.granularity;
  const hiddenVertices = editMode.hiddenVertices;
  const hiddenFaces = editMode.hiddenFaces;

  // Surface hit-testing owns all mesh edit picking. Vertex dots are visual-only:
  // the hit mesh resolves vertex/edge/face intent from the same BVH-backed ray.
  const surfaceClickEnabled = true;

  return (
    <group
      ref={editGroupRef}
      position={worldTransform.position}
      rotation={worldTransform.rotation}
      scale={worldTransform.scale}
      userData={{ excludeFromExport: true }}
    >
      <EditModeEdges
        objectId={editMode.objectId}
        vertices={obj.vertices}
        topology={obj.topology}
        granularity={granularity}
        hiddenVertices={hiddenVertices}
        hiddenFaces={hiddenFaces}
        dirtyVertices={dirtyVertices}
        xray={xray}
      />
      <FlaggedEdgeOverlay
        objectId={editMode.objectId}
        vertices={obj.vertices}
        topology={obj.topology}
        dirtyVertices={dirtyVertices}
        xray={xray}
      />
      {editMode.showVertexGroupColors &&
        obj.vertexGroups &&
        obj.vertexGroups.length > 0 && (
          <VertexGroupColorOverlay
            objectId={editMode.objectId}
            vertices={obj.vertices}
            topology={obj.topology}
            vertexGroups={obj.vertexGroups}
            hiddenFaces={hiddenFaces}
            dirtyVertices={dirtyVertices}
            xray={xray}
          />
        )}
      {editMode.showFaceGroupColors &&
        obj.faceAttributes?.groups &&
        obj.faceAttributes.groups.length > 0 && (
          <FaceGroupColorOverlay
            objectId={editMode.objectId}
            vertices={obj.vertices}
            topology={obj.topology}
            groups={obj.faceAttributes.groups}
            hiddenFaces={hiddenFaces}
            dirtyVertices={dirtyVertices}
            xray={xray}
          />
        )}
      {editMode.showMaterialSlotColors && obj.materialSlots.length > 1 && (
        <MaterialSlotColorOverlay
          objectId={editMode.objectId}
          vertices={obj.vertices}
          topology={obj.topology}
          materialSlots={obj.materialSlots}
          faceMaterialIndex={obj.faceAttributes?.materialIndex}
          hiddenFaces={hiddenFaces}
          dirtyVertices={dirtyVertices}
          xray={xray}
        />
      )}
      <SelectionHighlight
        objectId={editMode.objectId}
        vertices={obj.vertices}
        topology={obj.topology}
        selectedVertices={editMode.selectedVertices}
        selectedEdges={editMode.selectedEdges}
        selectedFaces={editMode.selectedFaces}
        granularity={granularity}
        dirtyVertices={dirtyVertices}
        xray={xray}
      />
      {granularity === "vertex" && (
        <EditModeVertices
          objectId={editMode.objectId}
          vertices={obj.vertices}
          topology={obj.topology}
          selectedVertices={editMode.selectedVertices}
          hiddenVertices={hiddenVertices}
          dirtyVertices={dirtyVertices}
        />
      )}
      {granularity === "face" && (
        <FaceDots
          objectId={editMode.objectId}
          vertices={obj.vertices}
          normals={obj.loopNormals}
          topology={obj.topology}
          selectedFaces={editMode.selectedFaces}
          hiddenFaces={hiddenFaces}
          dirtyVertices={dirtyVertices}
        />
      )}
      <SelectionHitTest
        objectId={editMode.objectId}
        vertices={obj.vertices}
        indices={obj.indices}
        topology={obj.topology}
        granularity={granularity}
        clickEnabled={surfaceClickEnabled}
        hiddenVertices={hiddenVertices}
        hiddenFaces={hiddenFaces}
        xray={xray}
      />
      <WireEdgeSelector
        vertices={obj.vertices}
        topology={obj.topology}
        granularity={granularity}
        hiddenVertices={hiddenVertices}
      />
      <EdgeSlideOverlay vertices={obj.vertices} />
    </group>
  );
}

// ---------------------------------------------------------------------------
// Edge wireframe — polygon edges only (no triangle diagonals)
// ---------------------------------------------------------------------------

function EditModeEdges({
  objectId,
  vertices,
  topology,
  granularity,
  hiddenVertices,
  hiddenFaces,
  dirtyVertices,
  xray,
}: {
  objectId: string;
  vertices: Float32Array;
  topology: PolygonTopology;
  granularity: "vertex" | "edge" | "face";
  hiddenVertices: Set<number>;
  hiddenFaces: Set<number>;
  dirtyVertices: Uint32Array | null;
  xray: boolean;
}) {
  const { invalidate } = useThree();
  const preview = useResolvedLiveEditGeometry(
    objectId,
    vertices,
    dirtyVertices,
  );
  const previewVertices = preview.vertices;
  const previewDirtyVertices = preview.dirtyVertices;
  const previewDirtyPositions = preview.dirtyVertexPositions;
  // Edge indices (flat [v0,v1, v0,v1, ...]) — stable during vertex drags,
  // only changes with topology. Filter out edges that belong only to hidden faces.
  const edgeIndices = useMemo(() => {
    const { edges } = topology;
    if (hiddenVertices.size === 0 && hiddenFaces.size === 0) return edges;
    if (hiddenFaces.size === 0) {
      // Vertex-only hiding: edge hidden when both endpoints hidden
      const kept: number[] = [];
      const edgeCount = edges.length / 2;
      for (let i = 0; i < edgeCount; i++) {
        const a = edges[i * 2],
          b = edges[i * 2 + 1];
        if (!hiddenVertices.has(a) || !hiddenVertices.has(b)) {
          kept.push(a, b);
        }
      }
      return new Uint32Array(kept);
    }
    // Build edge→face adjacency: an edge is hidden if ALL its adjacent faces are hidden
    const edgeFaces = new Map<number, number[]>();
    const faceCount = topology.offsets.length - 1;
    for (let fi = 0; fi < faceCount; fi++) {
      const verts = topologyFaceVerts(topology, fi);
      for (let j = 0; j < verts.length; j++) {
        const ei = findEdgeIndex(
          topology,
          verts[j],
          verts[(j + 1) % verts.length],
        );
        if (ei < 0) continue;
        let list = edgeFaces.get(ei);
        if (!list) {
          list = [];
          edgeFaces.set(ei, list);
        }
        list.push(fi);
      }
    }
    const kept: number[] = [];
    const edgeCount = edges.length / 2;
    for (let i = 0; i < edgeCount; i++) {
      const a = edges[i * 2],
        b = edges[i * 2 + 1];
      const ei = findEdgeIndex(topology, a, b);
      const faces = ei >= 0 ? edgeFaces.get(ei) : undefined;
      const visible = faces
        ? !faces.every((fi) => hiddenFaces.has(fi))
        : !hiddenVertices.has(a) || !hiddenVertices.has(b);
      if (visible) kept.push(a, b);
    }
    return new Uint32Array(kept);
  }, [topology, hiddenVertices, hiddenFaces]);

  const edgePairCount = edgeIndices.length / 2;
  const fatSegmentIndex = useMemo(
    () =>
      granularity === "edge"
        ? buildVertexSegmentIndex(previewVertices.length / 3, edgeIndices)
        : null,
    [granularity, edgeIndices, previewVertices.length],
  );

  // Thin-line geometry (vertex/face modes) — topology-dependent structure
  const thinGeo = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array(previewVertices), 3),
    );
    geo.setIndex(new THREE.BufferAttribute(new Uint32Array(edgeIndices), 1));
    return geo;
  }, [edgeIndices, previewVertices.length, previewVertices]); // eslint-disable-line react-hooks/exhaustive-deps
  useDispose(thinGeo);

  // Update thin-line vertex positions in-place during drag
  useEffect(() => {
    const posAttr = thinGeo.getAttribute("position") as THREE.BufferAttribute;
    if (posAttr.array.length === previewVertices.length) {
      updateSharedVertexPositions(
        posAttr,
        previewVertices,
        previewDirtyVertices,
        previewDirtyPositions,
      );
    } else {
      thinGeo.setAttribute(
        "position",
        new THREE.BufferAttribute(new Float32Array(previewVertices), 3),
      );
    }
  }, [previewVertices, thinGeo, previewDirtyVertices, previewDirtyPositions]);

  // Fat-line geometry (edge mode) — LineSegmentsGeometry wants flat position pairs.
  // Positions are held in a ref and mutated in place per drag tick to avoid
  // allocating a fresh Float32Array every frame. The ghost-edge/operator-preview
  // bug came from reusing the same LineSegmentsGeometry across topology changes:
  // invalidation alone was not enough under WebGPU once the segment count changed.
  const fatPositionsRef = useRef<Float32Array | null>(null);
  const fatGeo = useMemo(() => {
    if (granularity !== "edge") return null;
    fatPositionsRef.current = null;
    return new LineSegmentsGeometry();
  }, [granularity, edgePairCount]);

  // Populate / update fat-line vertex positions in-place during drag
  useLayoutEffect(() => {
    if (!fatGeo || granularity !== "edge") return;
    const needed = edgePairCount * 6;
    let positions = fatPositionsRef.current;
    const resized = !positions || positions.length !== needed;
    if (resized) {
      positions = new Float32Array(needed);
      fatPositionsRef.current = positions;
    }
    const p = positions!;
    const updateRanges =
      resized || !previewDirtyVertices
        ? null
        : resolveFatLineUpdateRanges(fatSegmentIndex, previewDirtyVertices);
    writeFatLineSegmentPositions(
      p,
      edgeIndices,
      previewVertices,
      updateRanges,
      previewDirtyVertices,
      previewDirtyPositions,
    );
    if (updateRanges) setFatLinePositions(fatGeo, p, updateRanges, false);
    else setFatLinePositions(fatGeo, p, null, false);
    invalidate();
  }, [
    edgeIndices,
    edgePairCount,
    fatGeo,
    fatSegmentIndex,
    granularity,
    previewDirtyVertices,
    previewDirtyPositions,
    previewVertices,
    invalidate,
  ]);

  const fatMat = useMemo(() => {
    if (granularity !== "edge") return null;
    return new THREE.Line2NodeMaterial({
      color: new THREE.Color(EDGE_COLOR_PROMINENT).getHex(),
      linewidth: 2,
      transparent: true,
      opacity: 0.85,
    });
  }, [granularity]);

  // Mutate depthTest on the existing material rather than recreating it —
  // Line2NodeMaterial pipeline recompilation on disposal blanks the viewport.
  useEffect(() => {
    if (fatMat) fatMat.depthTest = !xray;
  }, [fatMat, xray]);

  // Thin-line material with clip-space overlay bias
  const thinMat = useMemo(
    () =>
      createOverlayLineMaterial({
        color: EDGE_COLOR,
        opacity: 0.6,
        depthTest: !xray,
      }),
    [xray],
  );

  // Stable LineSegments2 instance — reusing the object prevents R3F from
  // swapping it into the scene graph on every drag tick.
  const fatLines = useMemo(() => {
    if (!fatGeo || !fatMat) return null;
    const lines = new LineSegments2(fatGeo, fatMat);
    lines.frustumCulled = false;
    return lines;
  }, [fatGeo, fatMat]);

  // Clean up resources
  useDisposeMany(fatGeo, fatMat);
  useDispose(thinMat);

  if (granularity === "edge" && fatLines) {
    return <primitive object={fatLines} />;
  }

  return <lineSegments geometry={thinGeo} material={thinMat} />;
}

// ---------------------------------------------------------------------------
// Flagged-edge overlay — colors every edge with EDGE_SEAM or EDGE_SHARP
// regardless of selection state. Sits between the base wireframe and the
// selection highlight so user-marked edges stay legible but never outrank
// the live selection.
//
// Deliberately minimal: thin-line only (Blender does the same for seam /
// sharp), no hidden-edge filter (marks on hidden geometry are a rare edge
// case), and no fat-line variant (mark visibility doesn't depend on pick
// precision). Topology-flag rebuilds only trigger when `topology.edgeFlags`
// is replaced — `withEdgeFlagsSet` / `withEdgeFlagsCleared` produce a new
// Uint8Array so the useMemo dep is a stable identity when nothing changes.
// ---------------------------------------------------------------------------

function FlaggedEdgeOverlay({
  objectId,
  vertices,
  topology,
  dirtyVertices,
  xray,
}: {
  objectId: string;
  vertices: Float32Array;
  topology: PolygonTopology;
  dirtyVertices: Uint32Array | null;
  xray: boolean;
}) {
  const preview = useResolvedLiveEditGeometry(
    objectId,
    vertices,
    dirtyVertices,
  );
  const previewVertices = preview.vertices;
  const previewDirtyVertices = preview.dirtyVertices;
  const previewDirtyPositions = preview.dirtyVertexPositions;
  const { seamIndices, sharpIndices } = useMemo(() => {
    const { edges, edgeFlags } = topology;
    const edgeCount = edgeFlags.length;
    let seamCount = 0;
    let sharpCount = 0;
    for (let i = 0; i < edgeCount; i++) {
      if (edgeFlags[i] & EDGE_SEAM) seamCount++;
      if (edgeFlags[i] & EDGE_SHARP) sharpCount++;
    }
    const seamIndices = seamCount > 0 ? new Uint32Array(seamCount * 2) : null;
    const sharpIndices =
      sharpCount > 0 ? new Uint32Array(sharpCount * 2) : null;
    let sw = 0;
    let hw = 0;
    for (let i = 0; i < edgeCount; i++) {
      const f = edgeFlags[i];
      if (seamIndices && f & EDGE_SEAM) {
        seamIndices[sw++] = edges[i * 2];
        seamIndices[sw++] = edges[i * 2 + 1];
      }
      if (sharpIndices && f & EDGE_SHARP) {
        sharpIndices[hw++] = edges[i * 2];
        sharpIndices[hw++] = edges[i * 2 + 1];
      }
    }
    return { seamIndices, sharpIndices };
  }, [topology]);

  return (
    <>
      <FlaggedEdgeLines
        vertices={previewVertices}
        edgeIndices={seamIndices}
        color={COLOR_EDGE_SEAM}
        dirtyVertices={previewDirtyVertices}
        dirtyVertexPositions={previewDirtyPositions}
        xray={xray}
      />
      <FlaggedEdgeLines
        vertices={previewVertices}
        edgeIndices={sharpIndices}
        color={COLOR_EDGE_SHARP}
        dirtyVertices={previewDirtyVertices}
        dirtyVertexPositions={previewDirtyPositions}
        xray={xray}
      />
    </>
  );
}

function FlaggedEdgeLines({
  vertices,
  edgeIndices,
  color,
  dirtyVertices,
  dirtyVertexPositions,
  xray,
}: {
  vertices: Float32Array;
  edgeIndices: Uint32Array | null;
  color: string;
  dirtyVertices: Uint32Array | null;
  dirtyVertexPositions: Float32Array | null;
  xray: boolean;
}) {
  const geo = useMemo(() => {
    if (!edgeIndices) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array(vertices), 3),
    );
    g.setIndex(new THREE.BufferAttribute(edgeIndices, 1));
    return g;
  }, [edgeIndices, vertices.length, vertices]); // eslint-disable-line react-hooks/exhaustive-deps
  useDispose(geo);

  // Mutate vertex positions in-place on drag — avoids a per-frame geometry
  // rebuild that would recompile the pipeline state.
  useEffect(() => {
    if (!geo) return;
    const posAttr = geo.getAttribute("position") as THREE.BufferAttribute;
    if (posAttr.array.length === vertices.length) {
      updateSharedVertexPositions(
        posAttr,
        vertices,
        dirtyVertices,
        dirtyVertexPositions,
      );
    } else {
      geo.setAttribute(
        "position",
        new THREE.BufferAttribute(new Float32Array(vertices), 3),
      );
    }
  }, [vertices, geo, dirtyVertices, dirtyVertexPositions]);

  const mat = useMemo(
    () => createOverlayLineMaterial({ color, opacity: 0.95, depthTest: !xray }),
    [color, xray],
  );
  useDispose(mat);

  if (!geo) return null;
  return <lineSegments geometry={geo} material={mat} />;
}

// ---------------------------------------------------------------------------
// Selection highlight — polygon edges and faces
// ---------------------------------------------------------------------------

function SelectionHighlight({
  objectId,
  vertices,
  topology,
  selectedVertices,
  selectedEdges: explicitEdges,
  selectedFaces: explicitFaces,
  granularity,
  dirtyVertices,
  xray,
}: {
  objectId: string;
  vertices: Float32Array;
  topology: PolygonTopology;
  selectedVertices: Set<number>;
  selectedEdges: Set<number>;
  selectedFaces: Set<number>;
  granularity: "vertex" | "edge" | "face";
  dirtyVertices: Uint32Array | null;
  xray: boolean;
}) {
  const { invalidate } = useThree();
  const preview = useResolvedLiveEditGeometry(
    objectId,
    vertices,
    dirtyVertices,
  );
  const previewVertices = preview.vertices;
  const previewDirtyVertices = preview.dirtyVertices;
  const previewDirtyPositions = preview.dirtyVertexPositions;
  // In face mode: derive highlighted edges from the explicit face set so
  // shared edges on unselected neighbor faces don't light up.
  // In edge mode: use the explicit edge set so perpendicular shared-vertex
  // edges aren't inferred from vertex membership alone.
  // In vertex mode: infer edges from selectedVertices as before.
  const selectedEdges = useMemo(() => {
    if (granularity === "face") {
      const edges = new Set<number>();
      for (const fi of explicitFaces) {
        const fv = topologyFaceVerts(topology, fi);
        for (let j = 0; j < fv.length; j++) {
          const ei = findEdgeIndex(topology, fv[j], fv[(j + 1) % fv.length]);
          if (ei >= 0) edges.add(ei);
        }
      }
      return edges;
    }
    if (granularity === "edge" && explicitEdges.size > 0) {
      return explicitEdges;
    }
    return classifyPolySelection(topology, selectedVertices).selectedEdges;
  }, [topology, selectedVertices, explicitEdges, explicitFaces, granularity]);
  const selectedFaces = explicitFaces;

  // Expand selected edge indices to vertex pairs for rendering. Packed into
  // a pre-sized Uint32Array (not a dynamic number[]) so the selection-change
  // rebuild stays O(E_selected) with no array-growth churn at 1-5M-face
  // scenes.
  const selectedEdgeIndices = useMemo(() => {
    if (selectedEdges.size === 0) return null;
    const vertIdx = new Uint32Array(selectedEdges.size * 2);
    let w = 0;
    for (const ei of selectedEdges) {
      const [v0, v1] = edgeVertices(topology.edges, ei);
      vertIdx[w++] = v0;
      vertIdx[w++] = v1;
    }
    return vertIdx;
  }, [selectedEdges, topology.edges]);
  const fatEdgeSegmentIndex = useMemo(
    () =>
      granularity === "edge" && selectedEdgeIndices
        ? buildVertexSegmentIndex(
            previewVertices.length / 3,
            selectedEdgeIndices,
          )
        : null,
    [granularity, previewVertices.length, selectedEdgeIndices],
  );

  // Thin highlighted edge geometry (vertex/face modes) — structure from selection
  const edgeGeo = useMemo(() => {
    if (!selectedEdgeIndices) return null;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array(previewVertices), 3),
    );
    // selectedEdgeIndices is already a Uint32Array — attach directly
    // (previously double-converted through new Uint32Array(...)).
    geo.setIndex(new THREE.BufferAttribute(selectedEdgeIndices, 1));
    return geo;
  }, [selectedEdgeIndices, previewVertices.length, previewVertices]); // eslint-disable-line react-hooks/exhaustive-deps
  useDispose(edgeGeo);

  // Update thin highlighted edge positions in-place during drag
  useEffect(() => {
    if (!edgeGeo) return;
    const posAttr = edgeGeo.getAttribute("position") as THREE.BufferAttribute;
    if (posAttr.array.length === previewVertices.length) {
      updateSharedVertexPositions(
        posAttr,
        previewVertices,
        previewDirtyVertices,
        previewDirtyPositions,
      );
    } else {
      edgeGeo.setAttribute(
        "position",
        new THREE.BufferAttribute(new Float32Array(previewVertices), 3),
      );
    }
  }, [previewVertices, edgeGeo, previewDirtyVertices, previewDirtyPositions]);

  // Fat highlighted edge geometry (edge mode) — structure from selection.
  // See fatGeo above for the mutation-in-place pattern rationale.
  const fatEdgePositionsRef = useRef<Float32Array | null>(null);
  const selectedEdgePairCount = selectedEdgeIndices
    ? selectedEdgeIndices.length / 2
    : 0;
  const fatEdgeGeo = useMemo(() => {
    if (granularity !== "edge" || !selectedEdgeIndices) return null;
    fatEdgePositionsRef.current = null;
    return new LineSegmentsGeometry();
  }, [granularity, selectedEdgePairCount]);

  // Populate / update fat highlighted edge positions in-place during drag
  useLayoutEffect(() => {
    if (!fatEdgeGeo || granularity !== "edge" || !selectedEdgeIndices) return;
    const needed = selectedEdgeIndices.length * 3;
    let positions = fatEdgePositionsRef.current;
    const resized = !positions || positions.length !== needed;
    if (resized) {
      positions = new Float32Array(needed);
      fatEdgePositionsRef.current = positions;
    }
    const p = positions!;
    const updateRanges =
      resized || !previewDirtyVertices
        ? null
        : resolveFatLineUpdateRanges(fatEdgeSegmentIndex, previewDirtyVertices);
    writeFatLineSegmentPositions(
      p,
      selectedEdgeIndices,
      previewVertices,
      updateRanges,
      previewDirtyVertices,
      previewDirtyPositions,
    );
    if (updateRanges) setFatLinePositions(fatEdgeGeo, p, updateRanges, false);
    else setFatLinePositions(fatEdgeGeo, p, null, false);
    invalidate();
  }, [
    fatEdgeGeo,
    fatEdgeSegmentIndex,
    granularity,
    previewDirtyVertices,
    previewDirtyPositions,
    previewVertices,
    selectedEdgeIndices,
    invalidate,
  ]);

  const fatEdgeMat = useMemo(() => {
    if (granularity !== "edge") return null;
    return new THREE.Line2NodeMaterial({
      color: new THREE.Color(COLOR_EDGE_HIGHLIGHT).getHex(),
      linewidth: 3,
      transparent: true,
      opacity: 0.9,
    });
  }, [granularity]);

  useEffect(() => {
    if (fatEdgeMat) fatEdgeMat.depthTest = !xray;
  }, [fatEdgeMat, xray]);

  // Thin highlight edge material with clip-space bias
  const thinHighlightMat = useMemo(
    () =>
      createOverlayLineMaterial({
        color: COLOR_EDGE_HIGHLIGHT,
        opacity: 0.9,
        depthTest: !xray,
      }),
    [xray],
  );

  // Face highlight material with clip-space bias
  const faceHighlightMat = useMemo(
    () =>
      createOverlayFaceMaterial({
        color: COLOR_FACE_HIGHLIGHT,
        opacity: 0.15,
        depthTest: !xray,
        side: xray ? THREE.DoubleSide : THREE.FrontSide,
      }),
    [xray],
  );

  // Stable LineSegments2 instance (see fatLines in EditMeshDots above).
  const fatEdgeLines = useMemo(() => {
    if (!fatEdgeGeo || !fatEdgeMat) return null;
    const lines = new LineSegments2(fatEdgeGeo, fatEdgeMat);
    lines.frustumCulled = false;
    return lines;
  }, [fatEdgeGeo, fatEdgeMat]);

  useDisposeMany(fatEdgeGeo, fatEdgeMat);
  useDispose(thinHighlightMat);
  useDispose(faceHighlightMat);

  // Highlighted face geometry — structure from selection/topology.
  // Pre-sizes the triangle index buffer by summing (faceSize - 2) over the
  // selected set, then writes fan-triangulated indices into a Uint32Array
  // directly (no dynamic number[] growth, no per-face array allocation from
  // topologyFaceVerts). At 100k selected faces the difference is tens of ms.
  const faceGeo = useMemo(() => {
    if (selectedFaces.size === 0) return null;
    const { offsets, corners } = topology;

    let triCount = 0;
    for (const fi of selectedFaces) {
      const size = offsets[fi + 1] - offsets[fi];
      if (size >= 3) triCount += size - 2;
    }

    const faceIdx = new Uint32Array(triCount * 3);
    let w = 0;
    for (const fi of selectedFaces) {
      const start = offsets[fi];
      const end = offsets[fi + 1];
      const size = end - start;
      if (size < 3) continue;
      const v0 = corners[start];
      for (let i = 1; i < size - 1; i++) {
        faceIdx[w++] = v0;
        faceIdx[w++] = corners[start + i];
        faceIdx[w++] = corners[start + i + 1];
      }
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array(previewVertices), 3),
    );
    geo.setIndex(new THREE.BufferAttribute(faceIdx, 1));
    return geo;
  }, [topology, selectedFaces, previewVertices.length, previewVertices]); // eslint-disable-line react-hooks/exhaustive-deps
  useDispose(faceGeo);

  // Update face highlight positions in-place during drag
  useEffect(() => {
    if (!faceGeo) return;
    const posAttr = faceGeo.getAttribute("position") as THREE.BufferAttribute;
    if (posAttr.array.length === previewVertices.length) {
      updateSharedVertexPositions(
        posAttr,
        previewVertices,
        previewDirtyVertices,
        previewDirtyPositions,
      );
    } else {
      faceGeo.setAttribute(
        "position",
        new THREE.BufferAttribute(new Float32Array(previewVertices), 3),
      );
    }
  }, [previewVertices, faceGeo, previewDirtyVertices, previewDirtyPositions]);

  return (
    <>
      {granularity === "edge" && fatEdgeLines ? (
        <primitive object={fatEdgeLines} renderOrder={1} />
      ) : (
        edgeGeo && (
          <lineSegments
            geometry={edgeGeo}
            material={thinHighlightMat}
            renderOrder={1}
          />
        )
      )}
      {faceGeo && (
        <mesh geometry={faceGeo} material={faceHighlightMat} renderOrder={0} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Face centroid dots — shown in face selection mode (like Blender)
// ---------------------------------------------------------------------------

const _faceDummy = new THREE.Object3D();

function FaceDots({
  objectId,
  vertices,
  normals,
  topology,
  selectedFaces,
  hiddenFaces,
  dirtyVertices,
}: {
  objectId: string;
  vertices: Float32Array;
  normals: Float32Array;
  topology: PolygonTopology;
  selectedFaces: Set<number>;
  hiddenFaces: Set<number>;
  dirtyVertices: Uint32Array | null;
}) {
  const { camera, size: viewportSize } = useThree();
  const preview = useResolvedLiveEditGeometry(
    objectId,
    vertices,
    dirtyVertices,
  );
  const previewVertices = preview.vertices;
  const previewDirtyVertices = preview.dirtyVertices;
  const previewDirtyPositions = preview.dirtyVertexPositions;

  const targetBudget = overlayBudgetForViewport(
    viewportSize.width,
    viewportSize.height,
    "face",
  );
  const refinedBudget = useRefinedOverlayBudget(
    targetBudget,
    `${topology.offsets.length}:${selectedFaces.size}:${hiddenFaces.size}:face`,
  );

  // Visible face indices — excludes hidden faces
  const visibleFaces = useMemo(() => {
    return buildBudgetedFaceOverlay(
      topology,
      selectedFaces,
      hiddenFaces,
      refinedBudget,
    );
  }, [topology, selectedFaces, hiddenFaces, refinedBudget]);

  const visibleCount = visibleFaces.length;
  const faceInstanceIndex = useMemo(
    () =>
      buildVertexFaceInstanceIndex(
        topology,
        visibleFaces,
        previewVertices.length / 3,
      ),
    [topology, visibleFaces, previewVertices.length],
  );

  // Compute face normals for GPU back-face culling by averaging the
  // per-corner loop normals. loopNormals is indexed by corner position
  // (topology.offsets), NOT by vertex index — the same vertex can have
  // different normals on adjacent faces (auto-smooth).
  const faceNormals = useMemo(() => {
    const out = new Float32Array(visibleCount * 3);
    const { offsets } = topology;
    for (let i = 0; i < visibleCount; i++) {
      const fi = visibleFaces[i];
      const start = offsets[fi];
      const end = offsets[fi + 1];
      let nx = 0,
        ny = 0,
        nz = 0;
      for (let c = start; c < end; c++) {
        nx += normals[c * 3];
        ny += normals[c * 3 + 1];
        nz += normals[c * 3 + 2];
      }
      const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
      if (len > 1e-10) {
        nx /= len;
        ny /= len;
        nz /= len;
      }
      out[i * 3] = nx;
      out[i * 3 + 1] = ny;
      out[i * 3 + 2] = nz;
    }
    return out;
  }, [normals, topology, visibleFaces, visibleCount]);

  const faceSelected = useMemo(() => {
    const sel = new Uint8Array(visibleCount); // zero-initialized
    for (let i = 0; i < visibleCount; i++) {
      if (selectedFaces.has(visibleFaces[i])) sel[i] = 1;
    }
    return sel;
  }, [selectedFaces, visibleFaces, visibleCount]);

  const meshRef = useRef<THREE.InstancedMesh>(null);
  const positionsInitializedRef = useRef(false);

  // Visual dot geometry with per-instance scale attributes (no caps needed)
  const visualGeo = useMemo(
    () => createDotGeometry(visibleCount),
    [visibleCount],
  );
  useDispose(visualGeo);
  useEffect(() => {
    positionsInitializedRef.current = false;
  }, []);

  // Build translation-only instance matrices (scale handled by GPU uniform)
  const rebuildPositions = useCallback(() => {
    const mesh = meshRef.current;
    if (!mesh || visibleCount === 0) return;
    const dirtyVertexOffsets = buildDirtyVertexPositionMap(
      previewDirtyVertices,
      previewDirtyPositions,
    );
    let min = visibleCount;
    let max = -1;
    let sparseInstances: number[] | null = null;
    if (
      positionsInitializedRef.current &&
      previewDirtyVertices &&
      previewDirtyVertices.length > 0
    ) {
      const { offsets, instanceIndices } = faceInstanceIndex;
      const touchedInstances = new Set<number>();
      for (let i = 0; i < previewDirtyVertices.length; i++) {
        const vertexIndex = previewDirtyVertices[i]!;
        if (vertexIndex + 1 >= offsets.length) continue;
        for (
          let instanceOffset = offsets[vertexIndex];
          instanceOffset < offsets[vertexIndex + 1];
          instanceOffset++
        ) {
          touchedInstances.add(instanceIndices[instanceOffset]!);
        }
      }
      if (touchedInstances.size === 0) return;
      sparseInstances = [...touchedInstances];
      for (const instanceIndex of sparseInstances) {
        writeFaceDotMatrix(
          mesh,
          instanceIndex,
          visibleFaces[instanceIndex]!,
          previewVertices,
          topology,
          dirtyVertexOffsets,
          previewDirtyPositions,
        );
        if (instanceIndex < min) min = instanceIndex;
        if (instanceIndex > max) max = instanceIndex;
      }
    } else {
      for (let i = 0; i < visibleCount; i++) {
        writeFaceDotMatrix(
          mesh,
          i,
          visibleFaces[i]!,
          previewVertices,
          topology,
          dirtyVertexOffsets,
          previewDirtyPositions,
        );
      }
      min = 0;
      max = visibleCount - 1;
    }
    if (sparseInstances) {
      markInstanceMatrixUpdateRanges(mesh, sparseInstances);
    } else {
      markInstanceMatrixUpdateRange(mesh, min, max);
    }
    positionsInitializedRef.current = true;
  }, [
    faceInstanceIndex,
    previewDirtyVertices,
    previewDirtyPositions,
    previewVertices,
    topology,
    visibleCount,
    visibleFaces,
  ]);

  // Rebuild when data changes
  useEffect(() => {
    rebuildPositions();
  }, [rebuildPositions]);

  // Per-instance colors — written to the geometry-level dotColor attribute
  useEffect(() => {
    if (visibleCount === 0) return;
    for (let i = 0; i < visibleCount; i++) {
      setDotColor(visualGeo, i, faceSelected[i] ? _colorSel : _colorDef);
    }
    const attr = visualGeo.getAttribute(
      "dotColor",
    ) as THREE.InstancedBufferAttribute;
    attr.needsUpdate = true;
  }, [faceSelected, visibleCount, visualGeo]);

  // Per-instance normals for GPU back-face culling
  useEffect(() => {
    if (visibleCount === 0) return;
    setDotNormals(visualGeo, faceNormals, visibleCount);
  }, [visualGeo, faceNormals, visibleCount]);

  // Screen-space constant sizing — just update the GPU uniform (O(1))
  useFrame(() => {
    if (visibleCount === 0) return;
    const mesh = meshRef.current;
    if (!mesh) return;

    const worldPos = occ.worldPos;
    mesh.getWorldPosition(worldPos);
    dotBaseScale.value =
      screenToWorldRadius(
        VERTEX_SCREEN_PX,
        worldPos,
        camera,
        viewportSize.height,
      ) / VERTEX_RADIUS;
  });

  if (visibleCount === 0) return null;

  return (
    <instancedMesh
      ref={meshRef}
      args={[visualGeo, dotMaterial, visibleCount]}
      key={visibleCount}
      renderOrder={2}
      raycast={() => {}}
    />
  );
}

// ---------------------------------------------------------------------------
// Wire edge selector — invisible cylinder hit meshes for EDGE_WIRE edges.
//
// Wire edges carry no face topology so Three.js's mesh raycaster (used by
// SelectionHitTest) never fires on them. This component mounts one invisible
// cylinder per wire edge so Three.js CAN hit-test them. Because the cylinder
// registers as a proper Three.js hit, onPointerMissed does NOT fire after a
// wire-edge click — the same guarantee that SelectionHitTest provides for
// face-topology meshes. Active only in edge granularity mode.
// ---------------------------------------------------------------------------

function EditWireEdgeHitTarget({
  v0,
  v1,
  vertices,
  handleEditClick,
}: {
  v0: number;
  v1: number;
  vertices: Float32Array;
  handleEditClick: (hit: EditClickHit, mods: EditClickModifiers) => void;
}) {
  const pointerDownRef = useRef<{ x: number; y: number } | null>(null);

  const handlePointerDown = useCallback((e: ThreeEvent<PointerEvent>) => {
    pointerDownRef.current = {
      x: e.nativeEvent.clientX,
      y: e.nativeEvent.clientY,
    };
  }, []);

  const handlePointerUp = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      if (e.nativeEvent.button !== 0) return;
      const down = pointerDownRef.current;
      pointerDownRef.current = null;
      if (!down) return;
      const ddx = e.nativeEvent.clientX - down.x;
      const ddy = e.nativeEvent.clientY - down.y;
      if (ddx * ddx + ddy * ddy > TAP_DRAG_THRESHOLD_SQ) return;
      // Prevent onPointerMissed from firing — this is what makes wire-edge
      // selection work. Without a face mesh to hit, Three.js treats every click
      // as a miss and calls clearVertexSelection(). Mirror SelectionHitTest:
      // only stop propagation when the gesture is confirmed as a tap (not a
      // drag), so orbit/pan drags still correctly clear selection.
      e.stopPropagation();
      handleEditClick(
        { vertices: [v0, v1], edge: [v0, v1] },
        {
          shift: e.nativeEvent.shiftKey || e.nativeEvent.metaKey,
          ctrl: e.nativeEvent.ctrlKey,
          alt: e.nativeEvent.altKey,
        },
      );
    },
    [v0, v1, handleEditClick],
  );

  return (
    <WireEdgeHitMesh
      vertices={vertices}
      v0={v0}
      v1={v1}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
    />
  );
}

function WireEdgeSelector({
  vertices,
  topology,
  granularity,
  hiddenVertices,
}: {
  vertices: Float32Array;
  topology: PolygonTopology;
  granularity: "vertex" | "edge" | "face";
  hiddenVertices: Set<number>;
}) {
  const handleEditClick = useStore((s) => s.handleEditClick);

  const wireEdges = useMemo(() => {
    const { edges, edgeFlags } = topology;
    const count = edges.length / 2;
    const pairs: Array<[number, number]> = [];
    for (let i = 0; i < count; i++) {
      if (edgeFlags[i] & EDGE_WIRE)
        pairs.push([edges[i * 2], edges[i * 2 + 1]]);
    }
    return pairs;
  }, [topology]);

  if (granularity !== "edge" || wireEdges.length === 0) return null;

  return (
    <>
      {wireEdges.map(([v0, v1]) => {
        if (hiddenVertices.has(v0) || hiddenVertices.has(v1)) return null;
        return (
          <EditWireEdgeHitTarget
            key={`${v0}-${v1}`}
            v0={v0}
            v1={v1}
            vertices={vertices}
            handleEditClick={handleEditClick}
          />
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Hit-test mesh for edge/face selection (uses polygon topology)
// ---------------------------------------------------------------------------

function closestVisibleVertexOnPolyFace(
  vertices: Float32Array,
  topology: PolygonTopology,
  faceIndex: number,
  point: [number, number, number],
  hiddenVertices: ReadonlySet<number>,
): number | null {
  const { offsets, corners } = topology;
  const start = offsets[faceIndex]!;
  const end = offsets[faceIndex + 1]!;
  let best = -1;
  let bestDist = Infinity;
  for (let c = start; c < end; c++) {
    const vi = corners[c]!;
    if (hiddenVertices.has(vi)) continue;
    const base = vi * 3;
    const dx = vertices[base]! - point[0];
    const dy = vertices[base + 1]! - point[1];
    const dz = vertices[base + 2]! - point[2];
    const d2 = dx * dx + dy * dy + dz * dz;
    if (d2 < bestDist) {
      best = vi;
      bestDist = d2;
    }
  }
  return best >= 0 ? best : null;
}

type SurfaceHitLike = {
  faceIndex?: number | null;
  object: THREE.Object3D;
  point: THREE.Vector3;
};

function SelectionHitTest({
  objectId,
  vertices,
  indices,
  topology,
  granularity,
  clickEnabled,
  hiddenVertices,
  hiddenFaces,
  xray,
}: {
  objectId: string;
  vertices: Float32Array;
  indices: RenderTriangleIndices;
  topology: PolygonTopology;
  granularity: "vertex" | "edge" | "face";
  /** When false, the mesh is mounted for raycast registration only — click handlers are suppressed. */
  clickEnabled: boolean;
  hiddenVertices: Set<number>;
  hiddenFaces: Set<number>;
  xray: boolean;
}) {
  const handleEditClick = useStore((s) => s.handleEditClick);
  const isPickingEdge = useStore(
    (s) =>
      s.commandBuffer?.type === "edgeLoopInsert" &&
      s.commandBuffer.edgeLoopInsertEdge == null,
  );
  const pointerDownPos = useRef<{
    x: number;
    y: number;
    time: number;
    pointerType: string;
  } | null>(null);

  const triToPolyMap = useMemo(
    () => getCachedTriToPolyMap(topology),
    [topology],
  );

  /** With shared vertices, each index is unique — return it directly. */
  const expandVert = useCallback((vi: number): number[] => [vi], []);

  const { camera, gl } = useThree();
  const meshRef = useRef<THREE.Mesh>(null);
  const cycleContextKey = `${objectId}:${granularity}:${vertices.length}:${topology.corners.length}:${topology.offsets.length}:${topology.edges.length}:${hiddenVertices.size}:${hiddenFaces.size}`;

  useEffect(() => {
    if (!xray) resetEditSelectionCycle();
    return () => resetEditSelectionCycle();
  }, [xray]);

  // Hit-test geometry — uses expanded per-corner positions since indices
  // reference corner indices (0..totalCorners-1), not shared vertex indices.
  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const expanded = expandForRendering(
      vertices,
      asLoopNormals(new Float32Array(topology.corners.length * 3)),
      topology,
    );
    geo.setAttribute(
      "position",
      new THREE.BufferAttribute(expanded.renderVertices, 3),
    );
    geo.setIndex(new THREE.BufferAttribute(new Uint32Array(indices), 1));
    geo.computeBoundingSphere();
    return geo;
  }, [indices, topology, vertices.length, vertices]); // eslint-disable-line react-hooks/exhaustive-deps
  useDispose(geometry);

  // BVH acceleration for edit-mode picking + occlusion. Plain THREE.Raycaster
  // is O(triangles) per ray; on a 1M-face mesh, box-select (which rays every
  // candidate vertex through isWorldPointOccluded) blocks for seconds.
  // Gated on commandBuffer === null so the worker doesn't re-build on every
  // pointermove frame during a drag — picking and occlusion don't fire
  // mid-drag anyway, so a stale BVH costs nothing there.
  const isCommandActive = useStore((s) => s.commandBuffer !== null);
  useAsyncBoundsTree(geometry, vertices, !isCommandActive);

  // Update hit-test vertex positions in-place. Gated on !isCommandActive
  // for the same reason `useAsyncBoundsTree` is gated above: picking and
  // occlusion don't fire mid-drag, so the per-frame O(corners) position
  // copy is wasted work. `vertices` reference identity changes every
  // pointermove frame (commandApplyLiveEdit calls
  // `set(patchSceneObjectAfterEdit(...))`), so without this gate the
  // effect would re-run every frame even though no consumer needs it.
  // When the command ends, `isCommandActive` flips false and the effect
  // re-fires with the post-command vertices — picking is correct from
  // the next frame onward. boundingSphere = null defers recompute to
  // the next raycast.
  useEffect(() => {
    if (isCommandActive) return;
    const { corners } = topology;
    const posAttr = geometry.getAttribute("position") as THREE.BufferAttribute;
    const posArr = posAttr.array as Float32Array;
    if (posArr.length === corners.length * 3) {
      for (let c = 0; c < corners.length; c++) {
        const vi = corners[c];
        posArr[c * 3] = vertices[vi * 3];
        posArr[c * 3 + 1] = vertices[vi * 3 + 1];
        posArr[c * 3 + 2] = vertices[vi * 3 + 2];
      }
      posAttr.needsUpdate = true;
    } else {
      const expanded = expandForRendering(
        vertices,
        asLoopNormals(new Float32Array(corners.length * 3)),
        topology,
      );
      geometry.setAttribute(
        "position",
        new THREE.BufferAttribute(expanded.renderVertices, 3),
      );
    }
    geometry.boundingSphere = null;
  }, [vertices, topology, geometry, isCommandActive]);

  // Register the surface mesh for occlusion testing by vertex selection.
  useEffect(() => {
    registerOcclusionMesh(meshRef.current);
    return () => registerOcclusionMesh(null);
  }, []);

  // Register hidden face data for occlusion testing — hidden faces should not
  // occlude visible vertices behind them.
  useEffect(() => {
    const triMap = hiddenFaces.size > 0 ? triToPolyMap : null;
    registerOcclusionHidden(hiddenFaces, triMap);
    return () => registerOcclusionHidden(new Set(), null);
  }, [hiddenFaces, triToPolyMap]);

  // Register raycast function for keyboard-initiated operations (L key).
  // Raycaster + NDC vector are hoisted out of the callback so we don't
  // allocate a fresh pair on every keyboard-triggered edit operation.
  const editRaycasterRef = useRef<THREE.Raycaster | null>(null);
  const editNdcRef = useRef<THREE.Vector2 | null>(null);
  const resolveSurfaceHit = useCallback(
    (hit: SurfaceHitLike): EditSelectionCycleCandidate | null => {
      if (hit.faceIndex == null) return null;
      const polyFaceIdx = triToPolyMap[hit.faceIndex];
      if (polyFaceIdx == null) return null;
      if (hiddenFaces.has(polyFaceIdx)) return null;

      const local = hit.object.worldToLocal(hit.point.clone());
      const point: [number, number, number] = [local.x, local.y, local.z];

      let v0: number, v1: number, hitFace: number;
      if (granularity === "edge") {
        const result = closestEdgeNearFace(
          vertices,
          topology,
          polyFaceIdx,
          point,
        );
        v0 = result.v0;
        v1 = result.v1;
        hitFace = result.faceIndex;
      } else {
        const result = closestEdgeOnPolyFace(
          vertices,
          topology,
          polyFaceIdx,
          point,
        );
        v0 = result.v0;
        v1 = result.v1;
        hitFace = polyFaceIdx;
      }

      if (hiddenVertices.has(v0) && hiddenVertices.has(v1)) return null;

      if (granularity === "vertex") {
        const hitVertex = closestVisibleVertexOnPolyFace(
          vertices,
          topology,
          polyFaceIdx,
          point,
          hiddenVertices,
        );
        if (hitVertex == null) return null;
        return {
          key: `vertex:${hitVertex}`,
          hit: {
            vertices: expandVert(hitVertex),
            edge: [v0, v1],
            faceIndex: hitFace,
          },
        };
      }

      const targetVertices = [
        ...new Set([...expandVert(v0), ...expandVert(v1)]),
      ];
      const key =
        granularity === "face"
          ? `face:${hitFace}`
          : `edge:${numEdgeKey(v0, v1)}`;
      return {
        key,
        hit: { vertices: targetVertices, edge: [v0, v1], faceIndex: hitFace },
      };
    },
    [
      expandVert,
      granularity,
      hiddenFaces,
      hiddenVertices,
      topology,
      triToPolyMap,
      vertices,
    ],
  );
  const collectXrayCycleCandidates = useCallback(
    (clientX: number, clientY: number): EditSelectionCycleCandidate[] => {
      const mesh = meshRef.current;
      if (!mesh) return [];
      if (!editRaycasterRef.current)
        editRaycasterRef.current = new THREE.Raycaster();
      if (!editNdcRef.current) editNdcRef.current = new THREE.Vector2();
      const raycaster = editRaycasterRef.current;
      const ndc = editNdcRef.current;
      const rect = gl.domElement.getBoundingClientRect();
      ndc.set(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(ndc, camera);
      const mat = mesh.material as THREE.Material;
      const wasVisible = mat.visible;
      mat.visible = true;
      const hits = raycaster.intersectObject(mesh);
      mat.visible = wasVisible;

      const candidates: EditSelectionCycleCandidate[] = [];
      const seen = new Set<string>();
      for (const hit of hits) {
        const candidate = resolveSurfaceHit(hit);
        if (!candidate || seen.has(candidate.key)) continue;
        seen.add(candidate.key);
        candidates.push(candidate);
      }
      return candidates;
    },
    [camera, gl, resolveSurfaceHit],
  );
  useEffect(() => {
    if (!editRaycasterRef.current)
      editRaycasterRef.current = new THREE.Raycaster();
    if (!editNdcRef.current) editNdcRef.current = new THREE.Vector2();
    const raycaster = editRaycasterRef.current;
    const ndc = editNdcRef.current;
    registerEditRaycast((clientX, clientY) => {
      const mesh = meshRef.current;
      if (!mesh) return null;
      const rect = gl.domElement.getBoundingClientRect();
      ndc.set(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(ndc, camera);
      const mat = mesh.material as THREE.Material;
      const wasVisible = mat.visible;
      mat.visible = true;
      const hits = raycaster.intersectObject(mesh);
      mat.visible = wasVisible;
      if (hits.length === 0) return null;
      return resolveSurfaceHit(hits[0])?.hit ?? null;
    });
    return () => registerEditRaycast(null);
  }, [camera, gl, resolveSurfaceHit]);

  const handlePointerDown = useCallback((e: ThreeEvent<PointerEvent>) => {
    pointerDownPos.current = {
      x: e.nativeEvent.clientX,
      y: e.nativeEvent.clientY,
      time: performance.now(),
      pointerType: e.nativeEvent.pointerType,
    };
  }, []);

  const handlePointerUp = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      if (e.nativeEvent.button !== 0) return; // only left-click selects
      const down = pointerDownPos.current;
      pointerDownPos.current = null;
      if (!down) return;
      const dx = e.nativeEvent.clientX - down.x;
      const dy = e.nativeEvent.clientY - down.y;
      if (dx * dx + dy * dy > TAP_DRAG_THRESHOLD_SQ) return;

      // If a pie menu is still open, its commit fires later in the bubble
      // phase on window. Don't treat this pointer-up as a face-selection click
      // — the user is interacting with the pie, not making a selection.
      const st = useStore.getState();
      if (
        st.extrudePieOpen ||
        st.viewPieOpen ||
        st.snapPieOpen ||
        st.renderMethodPieOpen
      )
        return;

      // Hold-to-add: on touch, a sustained hold (without drag) treats the
      // tap as additive so users can build selections without modifier keys.
      const holdToAdd =
        down.pointerType === "touch" &&
        performance.now() - down.time >= HOLD_TO_ADD_MS;
      const additiveModifier = e.nativeEvent.shiftKey || e.nativeEvent.metaKey;
      const cycleEligible =
        xray &&
        !holdToAdd &&
        !additiveModifier &&
        !e.nativeEvent.ctrlKey &&
        !e.nativeEvent.altKey;

      // NOTE: No back-face rejection here — the mesh renders DoubleSide so
      // faces are visible and selectable from both sides.

      e.stopPropagation();

      if (cycleEligible) {
        const candidates = collectXrayCycleCandidates(
          e.nativeEvent.clientX,
          e.nativeEvent.clientY,
        );
        const hit = resolveEditSelectionCycle(
          e.nativeEvent.clientX,
          e.nativeEvent.clientY,
          cycleContextKey,
          candidates,
        );
        if (!hit) return;
        handleEditClick(hit, {
          shift: false,
          ctrl: false,
          alt: false,
        });
        return;
      }

      noteNonCycleEditClick(e.nativeEvent.clientX, e.nativeEvent.clientY);

      const candidate = resolveSurfaceHit(e);
      if (!candidate) return;

      // Cmd/Meta acts as additive (like Shift) — standard macOS convention
      // and matches the touch modifier palette's Cmd button behavior.
      // Ctrl alone triggers shortest path.
      handleEditClick(candidate.hit, {
        shift: additiveModifier || holdToAdd,
        ctrl: e.nativeEvent.ctrlKey,
        alt: e.nativeEvent.altKey,
      });
    },
    [
      xray,
      collectXrayCycleCandidates,
      cycleContextKey,
      resolveSurfaceHit,
      handleEditClick,
    ],
  );

  // Hover handler: compute edge loop preview when in pick-edge mode
  const handlePointerMove = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      if (!isPickingEdge) return;
      const faceIndex = e.faceIndex;
      if (faceIndex == null) return;
      const polyFaceIdx = triToPolyMap[faceIndex];
      const local = e.object.worldToLocal(e.point.clone());
      const point: [number, number, number] = [local.x, local.y, local.z];
      const { v0, v1 } = closestEdgeOnPolyFace(
        vertices,
        topology,
        polyFaceIdx,
        point,
      );

      const cb = useStore.getState().commandBuffer;
      if (cb?.type === "edgeLoopInsert") {
        const cuts = (cb.params?.cuts as number) ?? 1;
        const preview = previewEdgeLoop(vertices, topology, v0, v1, cuts);
        useStore.setState({
          commandBuffer: {
            ...cb,
            edgeLoopHoverEdge: [v0, v1],
            edgeLoopPreview: preview,
          },
        });
      }
    },
    [vertices, topology, triToPolyMap, isPickingEdge],
  );

  const edgeLoopPreview = useStore((s) =>
    s.commandBuffer?.type === "edgeLoopInsert"
      ? s.commandBuffer.edgeLoopPreview
      : null,
  );

  // Build preview line geometry from segment pairs (every 2 vertices = 1 segment)
  const previewGeo = useMemo(() => {
    if (!edgeLoopPreview || edgeLoopPreview.length < 6) return null;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(edgeLoopPreview, 3));
    return geo;
  }, [edgeLoopPreview]);
  useDispose(previewGeo);

  return (
    <>
      <mesh
        ref={meshRef}
        geometry={geometry}
        raycast={acceleratedRaycast}
        onPointerDown={clickEnabled ? handlePointerDown : undefined}
        onPointerUp={clickEnabled ? handlePointerUp : undefined}
        onPointerMove={clickEnabled ? handlePointerMove : undefined}
      >
        <meshBasicMaterial visible={false} side={THREE.DoubleSide} />
      </mesh>
      {previewGeo && (
        <lineSegments geometry={previewGeo} renderOrder={3}>
          <lineBasicMaterial
            color="#ffcc00"
            transparent
            opacity={0.9}
            depthTest={false}
          />
        </lineSegments>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Vertex points — InstancedMesh
// ---------------------------------------------------------------------------

const _colorSel = new THREE.Color(COLOR_SELECTED);
const _colorDef = new THREE.Color(COLOR_DEFAULT);

function EditModeVertices({
  objectId,
  vertices,
  topology,
  selectedVertices,
  hiddenVertices,
  dirtyVertices,
}: {
  objectId: string;
  vertices: Float32Array;
  topology: PolygonTopology;
  selectedVertices: Set<number>;
  hiddenVertices: Set<number>;
  dirtyVertices: Uint32Array | null;
}) {
  const preview = useResolvedLiveEditGeometry(
    objectId,
    vertices,
    dirtyVertices,
  );
  const previewVertices = preview.vertices;
  const previewDirtyVertices = preview.dirtyVertices;
  const previewDirtyPositions = preview.dirtyVertexPositions;
  const { camera, size: viewportSize } = useThree();

  const vertexCount = previewVertices.length / 3;
  const targetBudget = overlayBudgetForViewport(
    viewportSize.width,
    viewportSize.height,
    "vertex",
  );
  const refinedBudget = useRefinedOverlayBudget(
    targetBudget,
    `${topology.edges.length}:${selectedVertices.size}:${hiddenVertices.size}:vertex`,
  );

  const uniqueIndices = useMemo(() => {
    if (Math.max(0, vertexCount - hiddenVertices.size) <= refinedBudget) {
      return hiddenVertices.size === 0
        ? getIdentityVertexIndices(vertexCount)
        : buildBudgetedVertexOverlay(
            topology,
            vertexCount,
            selectedVertices,
            hiddenVertices,
            refinedBudget,
          );
    }
    return buildBudgetedVertexOverlay(
      topology,
      vertexCount,
      selectedVertices,
      hiddenVertices,
      refinedBudget,
    );
  }, [topology, vertexCount, selectedVertices, hiddenVertices, refinedBudget]);
  const vertexToInstance = useMemo(() => {
    const map = new Int32Array(vertexCount);
    map.fill(-1);
    for (let i = 0; i < uniqueIndices.length; i++) {
      map[uniqueIndices[i]!] = i;
    }
    return map;
  }, [uniqueIndices, vertexCount]);

  const count = uniqueIndices.length;

  // Per-vertex nearest-neighbor distance (object-local units), O(V+E) via topology.
  // Used to cap dot size so vertices in dense regions don't overlap.
  // Recomputed when topology changes, good enough during drag.
  const nearestDist = useMemo(() => {
    const allDistances = getNearestVertexDistances(topology, previewVertices);
    const dists = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      dists[i] = allDistances[uniqueIndices[i]!] ?? 1;
    }
    return dists;
  }, [topology, previewVertices, uniqueIndices, count]);

  // Vertex dots do NOT use normal-based back-face culling. A vertex sits
  // at the intersection of multiple faces whose normals can point in very
  // different directions — any averaged normal is a compromise that fails
  // at oblique angles (e.g. cube corners where 2 of 3 faces are back-facing).
  // Blender uses the same design: geometric culling for face dots (where the
  // normal is unambiguous) and depth buffer only for vertex dots.
  // With near=0.1, depth precision handles occlusion reliably at normal
  // viewing distances.

  const visualRef = useRef<THREE.InstancedMesh>(null);
  const positionsInitializedRef = useRef(false);

  // Visual dot geometry with per-instance scale attributes
  const visualGeo = useMemo(() => createDotGeometry(count), [count]);
  useDispose(visualGeo);
  useEffect(() => {
    positionsInitializedRef.current = false;
  }, []);

  const computeVisualDotScale = useCallback(
    (object: THREE.Object3D) => {
      const worldPos = occ.worldPos;
      object.getWorldPosition(worldPos);
      return (
        screenToWorldRadius(
          VERTEX_SCREEN_PX,
          worldPos,
          camera,
          viewportSize.height,
        ) / VERTEX_RADIUS
      );
    },
    [camera, viewportSize.height],
  );

  // Set per-instance scale caps from nearest-neighbor distances.
  // Only needs updating when topology changes (nearestDist dependency).
  useEffect(() => {
    const capAttr = visualGeo.getAttribute(
      "instanceScaleCap",
    ) as THREE.InstancedBufferAttribute;
    if (!capAttr || capAttr.count < count) return;
    for (let i = 0; i < count; i++) {
      capAttr.setX(i, (nearestDist[i] * 0.45) / VERTEX_RADIUS);
    }
    capAttr.needsUpdate = true;
  }, [visualGeo, nearestDist, count]);

  // Build translation-only instance matrices (scale=1, handled by GPU uniform).
  const rebuildPositions = useCallback(() => {
    const vis = visualRef.current;
    if (vis && count > 0) {
      const dirtyVertexOffsets = buildDirtyVertexPositionMap(
        previewDirtyVertices,
        previewDirtyPositions,
      );
      let min = count;
      let max = -1;
      const sparseInstances: number[] = [];
      if (
        positionsInitializedRef.current &&
        previewDirtyVertices &&
        previewDirtyVertices.length > 0
      ) {
        for (let i = 0; i < previewDirtyVertices.length; i++) {
          const instanceIndex = vertexToInstance[previewDirtyVertices[i]!];
          if (instanceIndex < 0) continue;
          const repIdx = uniqueIndices[instanceIndex]!;
          const [vx, vy, vz] = readVertexPosition(
            previewVertices,
            repIdx,
            dirtyVertexOffsets,
            previewDirtyPositions,
          );
          _dummy.position.set(vx, vy, vz);
          _dummy.scale.setScalar(1);
          _dummy.updateMatrix();
          vis.setMatrixAt(instanceIndex, _dummy.matrix);
          sparseInstances.push(instanceIndex);
          if (instanceIndex < min) min = instanceIndex;
          if (instanceIndex > max) max = instanceIndex;
        }
      } else {
        for (let i = 0; i < count; i++) {
          const repIdx = uniqueIndices[i]!;
          const [vx, vy, vz] = readVertexPosition(
            previewVertices,
            repIdx,
            dirtyVertexOffsets,
            previewDirtyPositions,
          );
          _dummy.position.set(vx, vy, vz);
          _dummy.scale.setScalar(1);
          _dummy.updateMatrix();
          vis.setMatrixAt(i, _dummy.matrix);
        }
        min = 0;
        max = count - 1;
      }
      if (
        positionsInitializedRef.current &&
        previewDirtyVertices &&
        previewDirtyVertices.length > 0
      ) {
        markInstanceMatrixUpdateRanges(vis, sparseInstances);
      } else {
        markInstanceMatrixUpdateRange(vis, min, max);
      }
      positionsInitializedRef.current = true;
    }
  }, [
    previewVertices,
    uniqueIndices,
    vertexToInstance,
    count,
    previewDirtyVertices,
    previewDirtyPositions,
  ]);

  // Rebuild positions whenever vertex data changes.
  useEffect(() => {
    rebuildPositions();
  }, [rebuildPositions]);

  // Per-instance colors — written to the geometry-level dotColor attribute
  useEffect(() => {
    if (count === 0) return;
    for (let i = 0; i < count; i++) {
      const repIdx = uniqueIndices[i];
      const selected = selectedVertices.has(repIdx);
      setDotColor(visualGeo, i, selected ? _colorSel : _colorDef);
    }
    const attr = visualGeo.getAttribute(
      "dotColor",
    ) as THREE.InstancedBufferAttribute;
    attr.needsUpdate = true;
  }, [uniqueIndices, selectedVertices, count, visualGeo]);

  useFrame(() => {
    if (count === 0) return;
    const vis = visualRef.current;
    if (!vis) return;

    // Visual dots: just update the GPU uniform (O(1), no buffer uploads)
    dotBaseScale.value = computeVisualDotScale(vis);
  });

  if (count === 0) return null;

  return (
    <instancedMesh
      ref={visualRef}
      args={[visualGeo, dotMaterial, count]}
      key={count}
      renderOrder={3}
      raycast={() => {}}
    />
  );
}

// ---------------------------------------------------------------------------
// Edge slide rail overlay — highlights the edges vertices will slide along
// and shows a grab indicator at the selection centroid.
// ---------------------------------------------------------------------------

const GRAB_DOT_RADIUS = 0.018;
const GRAB_DOT_SPHERE = new THREE.SphereGeometry(GRAB_DOT_RADIUS, 12, 8);
const RAIL_LINE_MAT = createOverlayLineMaterial({
  color: COLOR_RAIL,
  opacity: 0.7,
  depthTest: true,
});

function EdgeSlideOverlay({ vertices }: { vertices: Float32Array }) {
  const commandBuffer = useStore((s) => s.commandBuffer);

  const rails =
    commandBuffer?.type === "edgeSlide" ? commandBuffer.edgeSlideRails : null;
  if (!rails || rails.size === 0) return null;

  return <EdgeSlideRails vertices={vertices} rails={rails} />;
}

function EdgeSlideRails({
  vertices,
  rails,
}: {
  vertices: Float32Array;
  rails: Map<number, EdgeSlideRail>;
}) {
  // Rail line geometry is static (depends only on rail structure, not vertex positions).
  const railGeo = useMemo(() => {
    const positions: number[] = [];
    const seen = new Set<string>();

    for (const [, rail] of rails) {
      const key = `${rail.origin[0]},${rail.origin[1]},${rail.origin[2]}`;
      if (seen.has(key)) continue;
      seen.add(key);
      positions.push(rail.origin[0], rail.origin[1], rail.origin[2]);
      positions.push(rail.a[0], rail.a[1], rail.a[2]);
      if (rail.b) {
        positions.push(rail.origin[0], rail.origin[1], rail.origin[2]);
        positions.push(rail.b[0], rail.b[1], rail.b[2]);
      }
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array(positions), 3),
    );
    return geo;
  }, [rails]);
  useDispose(railGeo);

  // Centroid tracks current vertex positions (cheap — just averaging).
  const centroid = useMemo<[number, number, number]>(
    () => vertexCentroid(vertices, rails.keys()) ?? [0, 0, 0],
    [vertices, rails],
  );

  return (
    <>
      {/* Rail edge highlights */}
      <lineSegments
        geometry={railGeo}
        material={RAIL_LINE_MAT}
        renderOrder={2}
      />

      {/* Grab indicator dot at selection centroid */}
      <mesh position={centroid} renderOrder={3}>
        <primitive object={GRAB_DOT_SPHERE} attach="geometry" />
        <meshBasicMaterial
          color={COLOR_GRAB_DOT}
          transparent
          opacity={0.85}
          depthTest={false}
        />
      </mesh>
    </>
  );
}
